import React, { Component } from 'react';

import Highlight from 'common/developers/docs/api/Highlight';
import devURL from 'common/util/devURL';

import APIDocsBlock from '../APIDocsBlock';
import APIDocsTable from '../APIDocsTable';

export default class APIDocsPostObjectBlock extends Component {
  render() {
    const { company } = this.props;
    const statusURL = devURL(
      `https://${company?.subdomain}.canny.io/admin/settings/roadmap/statuses`
    );

    return (
      <APIDocsBlock>
        <div>
          <div className="heading">The post object</div>
          <APIDocsTable
            title="Attributes"
            rows={[
              {
                description: 'A unique identifier for the post.',
                heading: 'id',
                subheading: 'string',
              },
              {
                description:
                  "The user who authored the post. If the author's account has been deleted, this field will be null.",
                heading: 'author',
                subheading: 'User object',
              },
              {
                description: 'The board this post is associated with.',
                heading: 'board',
                subheading: 'Board object',
              },
              {
                description: 'The user who created the post on behalf of the author.',
                heading: 'by',
                subheading: 'User object',
              },
              {
                description: 'The category this post is assigned to, if any.',
                heading: 'category',
                subheading: 'Category object',
              },
              {
                description: 'The number of non-deleted comments associated with this post.',
                heading: 'commentCount',
                subheading: 'number',
              },
              {
                description: 'Time at which the post was created, in ISO 8601 format.',
                heading: 'created',
                subheading: 'string',
              },
              {
                description: 'A list of Clickup tasks that are linked with this post',
                heading: 'clickup.linkedTasks',
                subheading: 'array',
              },
              {
                description:
                  'Any details the user included in the post. This is the longer text field (where the shorter one is "title").',
                heading: 'details',
                subheading: 'string',
              },
              {
                description: 'The month and year the post is estimated to be delivered.',
                heading: 'eta',
                subheading: 'string',
              },
              {
                description: 'An array of the URLs of the images associated with this post',
                heading: 'imageURLs',
                subheading: 'array',
              },
              {
                description: 'A list of Jira issues that are linked with this post',
                heading: 'jira.linkedIssues',
                subheading: 'array',
              },
              {
                description: 'The owner of the post',
                heading: 'owner',
                subheading: 'User',
              },
              {
                description: 'The roadmaps the post is associated with',
                heading: 'roadmaps',
                subheading: 'array',
              },
              {
                description: 'The number of votes that have been cast on this post.',
                heading: 'score',
                subheading: 'number',
              },
              {
                description: (
                  <div>
                    The post's status: "open", "under review", "planned", "in progress", "complete",
                    "closed", or any other status your team has set on the{' '}
                    {company?.subdomain ? (
                      <a href={statusURL}>settings&nbsp;page.</a>
                    ) : (
                      'settings\u00a0page.'
                    )}
                  </div>
                ),
                heading: 'status',
                subheading: 'string',
              },
              {
                description: "Time at which the post's status was last changed",
                heading: 'statusChangedAt',
                subheading: 'string',
              },
              {
                description: 'The list of tag objects associated with this post.',
                heading: 'tags',
                subheading: 'array',
              },
              {
                description:
                  'A brief title describing the post. This is the shorter text input (where the longer is details).',
                heading: 'title',
                subheading: 'string',
              },
              {
                description: "The URL to the post's page.",
                heading: 'url',
                subheading: 'string',
              },
            ]}
          />
        </div>
        <div>
          <div className="text">Example post object:</div>
          <Highlight lang="json">
            {`{
  "id": "553c3ef8b8cdcd1501ba1238",
  "author": {
    "id": "553c3ef8b8cdcd1501ba123a",
    "created": "2017-07-20T22:11:00.000Z",
    "email": "test@test.test",
    "isAdmin": true,
    "name": "Sally Doe",
    "url": "https://your-company.canny.io/admin/users/sally-doe",
    "userID": "1234"
  },
  "board": {
    "id": "553c3ef8b8cdcd1501ba1234",
    "created": "2017-07-15T22:11:00.000Z",
    "name": "Feature Requests",
    "postCount": 123,
    "url": "https://your-company.canny.io/admin/board/feature-requests"
  },
  "by": {
    "id": "524c3ef8b8cdcd1501ba246b",
    "created": "2017-07-15T22:11:00.000Z",
    "email": "test@john.test",
    "isAdmin": true,
    "name": "John Doe",
    "url": "https://your-company.canny.io/admin/users/john-doe",
    "userID": "5678"
  },
  "category": {
    "id": "553c3ef8b8cdcd1501ba2234",
    "name": "Dashboard",
    "parentID": null,
    "postCount": 42,
    "url": "https://your-company.canny.io/admin/board/feature-requests?category=dashboard"
  },
  "commentCount": 10,
  "created": "2017-08-03T22:11:00.000Z",
  "clickup": {
    "linkedTasks": [{
      "id": "123",
      "linkID": "2334jdsai23234io22",
      "name": "Clickup issue",
      "postID": "553c3ef8b8cdcd1501ba1238",
      "status": "to do",
      "url": "https://app.clickup.com/t/123456"
    }]
  },
  "details": "Test post details",
  "eta": "February 2020",
  "imageURLs": [
    "https://canny.io/images/93fc5808937760b82c3dc00aa5cd86b8.png",
    "https://canny.io/images/316e5600645b81e4be287a52d506dbfd.jpg"
  ],
  "jira": {
    "linkedIssues": [{
      "id": "123",
      "key": "ID-123",
      "url": "https://your-company.atlassian.net/browse/ID-123"
    }]
  },
  "owner": {
    "id": "553c3ef8b8cdcd1501ba123a",
    "created": "2017-07-20T22:11:00.000Z",
    "email": "test@test.test",
    "isAdmin": true,
    "name": "Sally Doe",
    "url": "https://your-company.canny.io/admin/users/sally-doe",
    "userID": "1234"
  },
  "score": 72,
  "status": "in progress",
  "tags": [{
    "id": "553c3ef8b8cdcd1501ba2234",
    "name": "iOS",
    "postCount": 15,
    "url": "https://your-company.canny.io/admin/board/feature-requests?tags=ios"
  }],
  "title": "An awesome feature request",
  "url": "https://your-company.canny.io/admin/board/feature-requests/p/an-awesome-feature-request"
}`}
          </Highlight>
        </div>
      </APIDocsBlock>
    );
  }
}
